import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Check if URL has client slug for branded login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSlug = params.get('client');
    if (clientSlug) {
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/clients/branding/${clientSlug}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setBranding(data); })
        .catch(() => {});
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      onLogin(data.user, data.token);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = branding?.primary_color || '#1f2a4d';
  const accentColor = branding?.accent_color || '#e01e25';
  const clientName = branding?.name;
  const clientLogo = branding?.logo_url;

  const mainContent = (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col items-center justify-center relative"
        style={{ background: primaryColor }}
      >
        <div className="absolute top-0 right-0 bottom-0 w-[6px]" style={{ background: accentColor }} />
        <div className="text-center">
          {clientLogo ? (
            <img src={clientLogo} alt={clientName} className="w-28 h-28 mx-auto mb-4 object-contain" />
          ) : (
            <img src="/logos/freddy-seal.png" alt="Freddy Media" className="w-36 h-36 mx-auto mb-4 object-contain" />
          )}
          {clientName && (
            <h2 className="text-xl font-semibold text-white mt-4 tracking-wide">{clientName}</h2>
          )}
          <p className="text-[11px] tracking-[0.2em] text-white/40 uppercase mt-2">
            {clientName ? 'Client Portal' : 'Productizing Creativity'}
          </p>
        </div>
        <div className="mt-16 max-w-xs text-center">
          <p className="text-white/70 text-sm leading-relaxed">
            {clientName
              ? `Welcome to your ${clientName} reporting dashboard. Sign in to view your analytics and reports.`
              : 'Your all-in-one social media reporting platform. Create stunning, branded reports with AI-powered insights.'
            }
          </p>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center bg-[hsl(var(--background))] px-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-10">
            {clientLogo ? (
              <img src={clientLogo} alt={clientName} className="h-12 mx-auto object-contain" />
            ) : (
              <img src="/logos/freddy-script.png" alt="Freddy" className="h-10 mx-auto object-contain" />
            )}
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Sign in
              </h2>
              <p className="text-sm text-muted-foreground mb-8">
                {clientName ? `Access your ${clientName} dashboard` : 'Enter your credentials to access the reporting suite'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="username">Email or Username</Label>
                  <Input
                    id="username"
                    data-testid="login-email-input"
                    placeholder="Enter your email or username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button type="button" onClick={() => setShowReset(true)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  data-testid="login-submit-button"
                  disabled={loading}
                  style={{ background: primaryColor }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {!clientName && (
            <div className="text-center mt-6">
              <img src="/logos/freddy-symbol.png" alt="" className="w-6 h-6 mx-auto opacity-30" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleResetRequest = async () => {
    try {
      const res = await api('/api/auth/password-reset-request', {
        method: 'POST', body: JSON.stringify({ email: resetEmail })
      });
      setResetSent(true);
      toast.success('If an account exists with this email, a reset link has been generated.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (showReset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-6">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Reset Password
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Enter your email to receive a password reset link
              </p>
              {resetSent ? (
                <div className="text-center py-4">
                  <p className="text-sm text-emerald-700 mb-4">Check your email for the reset link.</p>
                  <Button variant="outline" onClick={() => { setShowReset(false); setResetSent(false); }}>Back to Login</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                  <Button onClick={handleResetRequest} disabled={!resetEmail} className="w-full" style={{ background: primaryColor }}>
                    Send Reset Link
                  </Button>
                  <Button variant="ghost" className="w-full" onClick={() => setShowReset(false)}>Back to Login</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return mainContent;
}
